import React, { useEffect, useState } from 'react';
import { FiChevronDown, FiChevronRight, FiMenu, FiX } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';
import { Icon } from '../utils/Icon';

export interface NavItem {
  label: string;
  route?: string;
  items?: NavItem[];
}

interface AdminSideNavProps {
  title: string;
  items: NavItem[];
}

const findActive = (items: NavItem[], pathname: string): NavItem | undefined => {
  for (const item of items) {
    if (item.route === pathname) return item;
    const child = item.items && findActive(item.items, pathname);
    if (child) return child;
  }
  return undefined;
};

const AdminSideNav: React.FC<AdminSideNavProps> = ({ title, items }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const current = findActive(items, location.pathname);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const toggleItem = (label: string) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(label)) {
      newOpenItems.delete(label);
    } else {
      newOpenItems.add(label);
    }
    setOpenItems(newOpenItems);
  };

  const handleItemClick = (item: NavItem) => {
    if (item.items && item.items.length > 0) {
      toggleItem(item.label);
    } else if (item.route) {
      navigate(item.route);
      setMenuOpen(false);
    }
  };

  const isActive = (route?: string): boolean => {
    if (!route) return false;
    return location.pathname === route;
  };

  const renderItem = (item: NavItem, level: number = 0): React.ReactNode => {
    const hasChildren = item.items && item.items.length > 0;
    const isOpen = openItems.has(item.label);
    const active = isActive(item.route);
    const levelClass = level > 0 ? `admin-side-nav__button--level-${level}` : '';

    return (
      <li key={item.label} className="admin-side-nav__item">
        <button
          className={`admin-side-nav__button ${active ? 'admin-side-nav__button--active' : ''} ${levelClass}`}
          onClick={() => handleItemClick(item)}
          aria-current={active ? 'page' : undefined}
        >
          {hasChildren && (
            <Icon
              component={isOpen ? FiChevronDown : FiChevronRight}
              className="admin-side-nav__chevron"
            />
          )}
          <span className="admin-side-nav__label">{item.label}</span>
        </button>
        {hasChildren && isOpen && (
          <ul className="admin-side-nav__submenu">
            {item.items?.map((subItem) => renderItem(subItem, level + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <nav className="admin-side-nav">
      <p className="admin-side-nav__title">{title}</p>
      <button
        type="button"
        className="admin-side-nav__toggle"
        aria-expanded={menuOpen}
        aria-controls="admin-side-nav-list"
        aria-label={menuOpen ? t.common.closeDrawer : t.common.openDrawer}
        onClick={() => setMenuOpen((value) => !value)}
      >
        <span className="admin-side-nav__toggle-text">
          <span className="admin-side-nav__toggle-title">{title}</span>
          {current?.label}
        </span>
        <Icon component={menuOpen ? FiX : FiMenu} className="admin-side-nav__toggle-icon" aria-hidden />
      </button>
      <ul
        id="admin-side-nav-list"
        className={`admin-side-nav__list ${menuOpen ? 'admin-side-nav__list--open' : ''}`}
      >
        {items.map((item) => renderItem(item))}
      </ul>
    </nav>
  );
};

export default AdminSideNav;
